// AGC: useNoteMetadata — shared hook for GPS, description, team tags, and speech-to-text
// AGC: Used by PhotoCaptureModal, VideoCaptureModal, AudioNoteModal to avoid code duplication
// AGC: Speech-to-text: FREE tier → native speech, PRO/PREMIUM → Whisper API

import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from './useTranslation'
import { useSubscription } from '../context/SubscriptionContext'
import { getBackendUrl } from '../utils/backendUrl'
import type { GPSLocation } from '../types/lotNotes'

export interface NoteMetadata {
  // AGC: GPS state
  gpsLocation: GPSLocation | undefined
  gpsLoading: boolean
  gpsError: boolean
  captureGPS: () => Promise<void>

  // AGC: Description state
  description: string
  setDescription: (text: string) => void

  // AGC: Tagged users state
  taggedUserIds: string[]
  setTaggedUserIds: (ids: string[]) => void

  // AGC: Speech-to-text state
  isListening: boolean
  isProcessingSpeech: boolean
  startSpeechToText: (language?: string) => Promise<void>
  stopSpeechToText: () => Promise<void>
  toggleSpeechToText: (language?: string) => Promise<void>

  // AGC: Reset all state (call on modal close)
  reset: () => void
}

interface UseNoteMetadataOptions {
  // AGC: Auto-capture GPS when hook mounts (default true)
  autoCaptureGPS?: boolean
  // AGC: Whether the hook is active (tied to modal isOpen)
  isActive?: boolean
}

export const useNoteMetadata = (options: UseNoteMetadataOptions = {}): NoteMetadata => {
  const { autoCaptureGPS = true, isActive = true } = options
  const { i18n } = useTranslation()
  const { tier } = useSubscription()

  const [gpsLocation, setGpsLocation] = useState<GPSLocation | undefined>()
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsError, setGpsError] = useState(false)
  const [description, setDescription] = useState('')
  const [taggedUserIds, setTaggedUserIds] = useState<string[]>([])
  const [isListening, setIsListening] = useState(false)
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false)
  const mountedRef = useRef(true)

  // AGC: Track mount status for async safety
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // AGC: Auto-capture GPS when active
  useEffect(() => {
    if (isActive && autoCaptureGPS) {
      captureGPS()
    }
  }, [isActive])

  const captureGPS = useCallback(async () => {
    setGpsLoading(true)
    setGpsError(false)
    try {
      const { getCurrentPosition } = await import('../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 15000 })
      if (!mountedRef.current) return
      setGpsLocation({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy ?? 0,
        timestamp: new Date().toISOString(),
      })
    } catch {
      if (!mountedRef.current) return
      setGpsError(true)
    } finally {
      if (mountedRef.current) setGpsLoading(false)
    }
  }, [])

  // AGC: Refs for Whisper MediaRecorder — no nested promises, just refs
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const speechLang = i18n?.language === 'en' ? 'en-US' : i18n?.language === 'es' ? 'es-ES' : 'pt-BR'
  const useWhisper = tier !== 'free'

  // AGC: Click 1 — start recording mic audio
  const startSpeechToText = useCallback(async () => {
    if (recorderRef.current) return
    try {
      if (useWhisper) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        streamRef.current = stream
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus' : 'audio/webm'
        const recorder = new MediaRecorder(stream, { mimeType })
        chunksRef.current = []
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
        recorder.start(250)
        recorderRef.current = recorder
        setIsListening(true)
      } else {
        // AGC: FREE → native speech recognition (auto-stops when user pauses)
        setIsListening(true)
        const speech = await import('../services/nativeSpeechRecognition')
        const hasPerm = await speech.hasPermission()
        if (!hasPerm) {
          const granted = await speech.requestPermission()
          if (!granted) { if (mountedRef.current) setIsListening(false); return }
        }
        const result = await speech.recognizeSpeech(speechLang)
        if (!mountedRef.current) return
        if (result.success && result.transcript) {
          setDescription(prev => prev ? `${prev} ${result.transcript}` : result.transcript!)
        }
        setIsListening(false)
      }
    } catch (err) {
      console.warn('AGC: Failed to start speech recording', err)
      if (mountedRef.current) setIsListening(false)
    }
  }, [useWhisper, speechLang])

  // AGC: Click 2 — stop recording, send audio to Whisper, set description with transcript
  const stopSpeechToText = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') {
      recorderRef.current = null
      if (mountedRef.current) setIsListening(false)
      return
    }

    // AGC: Stop recorder and wait for the final dataavailable + stop events
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        streamRef.current?.getTracks().forEach(t => t.stop())
        streamRef.current = null
        recorderRef.current = null
        const mimeType = recorder.mimeType || 'audio/webm'
        const b = new Blob(chunksRef.current, { type: mimeType })
        resolve(b)
        chunksRef.current = []
      }
      recorder.stop()
    })

    if (blob.size < 1000) {
      if (mountedRef.current) setIsListening(false)
      return
    }

    // AGC: Show processing state while Whisper transcribes
    if (mountedRef.current) {
      setIsListening(false)
      setIsProcessingSpeech(true)
    }

    try {
      const backendUrl = getBackendUrl()
      const formData = new FormData()
      formData.append('audio', blob, `speech_${Date.now()}.webm`)
      const resp = await fetch(`${backendUrl}/api/v1/voice/transcribe`, {
        method: 'POST',
        body: formData,
      })
      const data = await resp.json()
      if (data.success && data.transcription && mountedRef.current) {
        setDescription(prev => prev ? `${prev} ${data.transcription}` : data.transcription)
      }
    } catch (err) {
      console.error('AGC: Whisper transcription failed', err)
    } finally {
      if (mountedRef.current) setIsProcessingSpeech(false)
    }
  }, [])

  // AGC: Toggle — click to start, click again to stop and transcribe
  const toggleSpeechToText = useCallback(async () => {
    if (recorderRef.current) {
      await stopSpeechToText()
    } else {
      await startSpeechToText()
    }
  }, [startSpeechToText, stopSpeechToText])

  const reset = useCallback(() => {
    setGpsLocation(undefined)
    setGpsLoading(false)
    setGpsError(false)
    setDescription('')
    setTaggedUserIds([])
    setIsListening(false)
    setIsProcessingSpeech(false)
  }, [])

  return {
    gpsLocation,
    gpsLoading,
    gpsError,
    captureGPS,
    description,
    setDescription,
    taggedUserIds,
    setTaggedUserIds,
    isListening,
    isProcessingSpeech,
    startSpeechToText,
    stopSpeechToText,
    toggleSpeechToText,
    reset,
  }
}
